import { useEffect, useMemo, useState } from "react";
import {
  Combobox,
  Option,
  makeStyles,
  useId,
  Input,
  ComboboxProps,
} from "@fluentui/react-components";

type ComponentProps = {
  options: { label: string; value: string }[];
  selectedDropDownValues: string[];
  setSelectedDropDownValues: (values: string[]) => void;
  placeholder?: string;
  onOpenChange?: (e: any, data: { open: boolean }) => void;
  style?: any;
  select_all?: boolean;
  fixed_width?: boolean;
  maxSelectionLimit?: number;
  multiSelect?: boolean;
};

const useStyles = makeStyles({
  root: {
    maxWidth: "100%",
    overflowX: "auto",
  },
  fixed: {
    width: "215px !important",
  },
  full: {
    width: "100%",
  },
  listbox: {
    maxHeight: "45vh !important",

    overflowY: "auto",
  },
});

const SearchableDropdown = (props: ComponentProps) => {
  const {
    options,
    selectedDropDownValues,
    setSelectedDropDownValues,
    placeholder,
    onOpenChange,
    style,
    select_all = true,
    fixed_width = true,
    maxSelectionLimit,
    multiSelect = true,
  } = props;

  const styles = useStyles();
  const comboId = useId("combo-multi");
  const [search, setSearch] = useState("");

  const uniqueOptions = useMemo(() => {
    return options.filter(
      (option, index, self) =>
        index === self.findIndex((t) => t.value === option.value)
    );
  }, [options]);

  const filteredOptions = useMemo(() => {
    return uniqueOptions.filter((opt) =>
      opt.label?.toLowerCase().includes(search.toLowerCase())
    );
  }, [search, uniqueOptions]);

  const handleOptionSelect: ComboboxProps["onOptionSelect"] = (_, data) => {
    const value = data.optionValue;

    if (!value) return;

    let updated = [...selectedDropDownValues];
    const allOptionValues = options.map((opt) => opt.value);

    if (value === "select-all") {
      if (updated.length === allOptionValues.length) {
        updated = [];
      } else {
        updated =
          maxSelectionLimit && allOptionValues.length > maxSelectionLimit
            ? allOptionValues.slice(0, maxSelectionLimit)
            : allOptionValues;
      }
    } else {
      if (updated.includes(value)) {
        updated = updated.filter((v) => v !== value);
      } else {
        if (!maxSelectionLimit || updated.length < maxSelectionLimit) {
          updated.push(value);
        }
      }
    }

    setSelectedDropDownValues(updated);
  };

  const selectedCount = selectedDropDownValues.length;
  const displayedOptions = selectedDropDownValues.slice(0, 2);
  const remainingCount = selectedCount > 2 ? selectedCount - 2 : 0;

  // const renderSelectedLabel = () => {
  //   const selectedLabels = displayedOptions
  //     .map((val) => {
  //       const opt = options.find((o) => o.value === val);
  //       return opt?.label || "";
  //     })
  //     .filter(Boolean);

  //   return selectedLabels.length
  //     ? remainingCount > 0
  //       ? `${selectedLabels.join(", ")}, +${remainingCount}`
  //       : selectedLabels.join(", ")
  //     : placeholder || "Select";
  // };

  return (
    <div className={styles.root}>
      <Combobox
        aria-labelledby={comboId}
        multiselect={multiSelect}
        value={search}
        selectedOptions={
          selectedDropDownValues.length === options.length && select_all
            ? ["select-all", ...selectedDropDownValues]
            : selectedDropDownValues
        }
        onOptionSelect={handleOptionSelect}
        onOpenChange={(e, data) => {
          setSearch("");
          onOpenChange?.(e, data);
        }}
        input={{
          as: "input", // optional
          onChange: (e) => setSearch((e.target as HTMLInputElement).value),
          placeholder:
            selectedDropDownValues.length > 0
              ? (() => {
                const selectedLabels = selectedDropDownValues
                  .map(
                    (val) => options.find((opt) => opt.value === val)?.label
                  )
                  .filter(Boolean);

                const displayed = selectedLabels.slice(0, 2).join(", ");
                const remaining = selectedLabels.length - 2;

                return remaining > 0
                  ? `${displayed}, +${remaining}`
                  : displayed;
              })()
              : placeholder || "Type to search...",
        }}
        listbox={{ className: styles.listbox }}
        className={fixed_width ? styles.fixed : styles.full}
      >
        {select_all && filteredOptions.length > 0 && (
          <Option value="select-all" key="select-all">
            Select All
          </Option>
        )}
        {filteredOptions.length === 0 && (
          <Option disabled>No matching records found for the entered search criteria</Option>
        )}
        {filteredOptions.map((option, index) => (
          <Option
            key={index}
            value={option.value}
            disabled={
              maxSelectionLimit &&
                !selectedDropDownValues.includes(option.value) &&
                selectedDropDownValues.length >= maxSelectionLimit
                ? true
                : false
            }
          >
            {option.label}
          </Option>
        ))}
      </Combobox>
    </div>
  );
};

export default SearchableDropdown;
